import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  FiUser, FiCode, FiCpu, FiBriefcase, FiAward, FiMail,
  FiTerminal, FiMusic, FiFileText,
} from 'react-icons/fi';
import { useWindowsStore } from '../../store/windowsStore';

const ICON_MAP = {
  user: FiUser,
  code: FiCode,
  cpu: FiCpu,
  briefcase: FiBriefcase,
  award: FiAward,
  mail: FiMail,
  terminal: FiTerminal,
  music: FiMusic,
  'file-text': FiFileText,
  file: FiFileText,
};

const TONE_COLOR = {
  cyan: 'var(--neon-cyan)',
  pink: 'var(--neon-pink)',
  violet: 'var(--neon-violet)',
  green: 'var(--neon-green)',
};

/**
 * Dock — barra inferior com apps abertos. Estilo macOS.
 *
 * Implementação: subscribe manual via useState/useEffect (não useWindowsStore).
 * Motivo: useWindowsStore com useShallow dispara "result of getSnapshot
 * should be cached" no React 18 useSyncExternalStore.
 *
 * Aqui usamos useState + subscribe, onde o state é uma chave serializável
 * (string) que muda quando o set de janelas abertas muda. Isso evita
 * completamente o problema de getSnapshot caching.
 */
export default function Dock() {
  // String estável que muda só quando o set de IDs abertos ou topZ mudam.
  const [snapshotKey, setSnapshotKey] = useState(() => {
    const s = useWindowsStore.getState();
    return computeKey(s.windows, s.topZ);
  });
  const lastKeyRef = useRef(snapshotKey);

  useEffect(() => {
    const unsubscribe = useWindowsStore.subscribe((s) => {
      const newKey = computeKey(s.windows, s.topZ);
      if (newKey !== lastKeyRef.current) {
        lastKeyRef.current = newKey;
        setSnapshotKey(newKey);
      }
    });
    return unsubscribe;
  }, []);

  // Lemos o estado fresco dentro do render — só renderiza quando snapshotKey muda.
  const { openApps, topZ } = useMemo(() => {
    const s = useWindowsStore.getState();
    const apps = [];
    for (const id in s.windows) {
      const w = s.windows[id];
      if (!w.isOpen) continue;
      apps.push({
        id,
        title: w.title,
        tone: w.tone || 'cyan',
        icon: w.icon || 'code',
        pinned: !!w.pinned,
        isMinimized: w.isMinimized,
        isOpen: true,
        z: w.z,
      });
    }
    return { openApps: apps, topZ: s.topZ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotKey]);

  // Lê estado fresco da store — evita stale `app` e o bug de `!app.isOpen`
  // (campo ausente → sempre truthy → só openWindow, nunca min/focus).
  const handleClick = useCallback((id) => {
    const s = useWindowsStore.getState();
    const w = s.windows[id];
    if (!w) return;

    if (!w.isOpen) {
      s.openWindow(id);
      return;
    }
    if (w.isMinimized) {
      s.restoreWindow(id);
      return;
    }
    if (w.pinned) {
      s.focusWindow(id);
      return;
    }
    if (w.z === s.topZ) {
      s.minimizeWindow(id);
    } else {
      s.focusWindow(id);
    }
  }, []);

  if (openApps.length === 0) return null;

  return (
    <div
      className="fixed bottom-[68px] left-1/2 -translate-x-1/2 z-[9500] flex items-end gap-1.5 px-2.5 py-2 rounded-2xl bg-[var(--bg-glass-strong)] backdrop-blur-2xl border border-[var(--neon-cyan)]/30 shadow-[0_0_30px_rgba(0,240,255,0.18)]"
      role="toolbar"
      aria-label="Dock"
    >
      {openApps.map((app) => {
        const Icon = ICON_MAP[app.icon] || FiCode;
        const color = TONE_COLOR[app.tone] || TONE_COLOR.cyan;
        const isFocused = !app.isMinimized && app.z === topZ;

        return (
          <button
            key={app.id}
            type="button"
            onClick={() => handleClick(app.id)}
            className="relative w-11 h-11 rounded-lg flex items-center justify-center bg-[var(--bg-glass)] border border-white/10 hover:border-[var(--neon-cyan)]/60 hover:bg-[var(--bg-card-hover)] hover:-translate-y-1 transition-all duration-200"
            aria-label={`Activate ${app.title}`}
            title={`${app.title}${app.pinned ? ' (pinned)' : ''}`}
          >
            <Icon size={18} style={{ color }} />
            <span
              className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
              style={{
                background: app.isMinimized ? 'var(--text-muted)' : color,
                boxShadow: app.isMinimized ? 'none' : `0 0 6px ${color}`,
                opacity: app.isMinimized ? 0.5 : 1,
              }}
            />
            {isFocused && (
              <span
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{ boxShadow: `inset 0 0 0 1px ${color}, 0 0 12px ${color}40` }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// String serializável que muda quando o que importa muda.
// Formato: "id1:min:z,id2:min:z,...|topZ"
function computeKey(windows, topZ) {
  const parts = [];
  for (const id in windows) {
    const w = windows[id];
    if (!w.isOpen) continue;
    parts.push(`${id}:${w.isMinimized ? 1 : 0}:${w.z}`);
  }
  parts.sort();
  return parts.join(',') + '|' + topZ;
}